import {
  RcfaComplianceTableData,
  complianceScoreColor,
  getComplianceScorePercentage
} from '@domain/rcfa'
import {GaugeBar} from '@hconnect/uikit/src/lib2'
import {Typography} from '@mui/material'
import React from 'react'

import {LocationCellContent} from './LocationCellContent'

export const RcfaComplianceTableKeys = {
  LOCATION: 'location',
  COMPLIANCE_SCORE_BAR: 'complianceScoreBar',
  COMPLIANCE_SCORE: 'complianceScore',
  UNRESOLVED_TASKS: 'unresolvedTasksCount',
  OVERDUE_TASKS: 'overdueTasksCount'
} as const

const tableCellDataTestIdGenerator = (id: string, columnKey: string) =>
  `table-cell-${id}-${columnKey}`

export type RcfaComplianceTableKey =
  (typeof RcfaComplianceTableKeys)[keyof typeof RcfaComplianceTableKeys]

export const RCFA_COMPLIANCE_TABLE_KEYS = Object.values(RcfaComplianceTableKeys)

export const renderRcfaComplianceTableCell = <T extends RcfaComplianceTableData>(
  item: T,
  columnIndex: number,
  columnKey: string
): React.ReactNode => {
  const completed = item.rcfaCompliance?.completedCount ?? 0
  const mandatory = item.rcfaCompliance?.totalMandatoryCount ?? 0
  const dataTestId = tableCellDataTestIdGenerator(item.id, columnKey)

  switch (columnKey) {
    case RcfaComplianceTableKeys.LOCATION: {
      return (
        <LocationCellContent
          name={item.name}
          countryCode={item.countryCode}
          dataTestId={dataTestId}
        />
      )
    }
    case RcfaComplianceTableKeys.COMPLIANCE_SCORE_BAR:
      return (
        <GaugeBar
          value={completed}
          maxValue={mandatory}
          color={complianceScoreColor(completed, mandatory)}
          customSubtitle={true}
        />
      )
    case RcfaComplianceTableKeys.COMPLIANCE_SCORE:
      return <ComplianceScore value={completed} maxValue={mandatory} dataTestId={dataTestId} />
    case RcfaComplianceTableKeys.UNRESOLVED_TASKS:
      return (
        <Typography align="right" data-test-id={dataTestId}>
          {item.rcfaCompliance?.unresolvedTasksCount ?? 0}
        </Typography>
      )
    case RcfaComplianceTableKeys.OVERDUE_TASKS:
      return (
        <Typography align="right" data-test-id={dataTestId}>
          {item.rcfaCompliance?.overdueTasksCount ?? 0}
        </Typography>
      )
  }

  return item[columnKey]
}

const ComplianceScore = ({
  value,
  maxValue,
  dataTestId
}: {
  value: number
  maxValue: number
  dataTestId: string
}) => {
  const showPercentage = maxValue > 0

  return (
    <Typography fontWeight={600} data-test-id={dataTestId}>
      {value}/
      <Typography component="span" fontSize="inherit" color="text.secondary">
        {maxValue}
      </Typography>
      {showPercentage && ` (${getComplianceScorePercentage(value, maxValue)}%)`}
    </Typography>
  )
}
